import { Button } from "@clockwise/design-system";
import { MoreVert } from "@clockwise/icons";
import { ResponseStatusEnum, VideoLink } from "@clockwise/schema";
import { Tooltip } from "@material-ui/core";
import classNames from "classnames";
import { DateTime } from "luxon";
import React from "react";
import { TrackingEvents, useTracking } from "../../util/analytics.util";
import { formatDuration } from "../../util/duration.util";
import { Profile } from "../../util/profile.util";
import { UserAvatar } from "../UserAvatar";
import { AvatarStack } from "../avatar-stack/AvatarStack";
import DropdownButton from "../dropdown-button/DropdownButton";
import { getConferencingIcon, getEventPreviewFormattedTime, sortAttendees } from "./utils";

type Props = {
  title: string;
  startTime: DateTime;
  endTime: DateTime;
  videoLink?: VideoLink | null;
  attendees: { profile: Profile; rsvp?: ResponseStatusEnum | null }[];
  isNow: boolean;
  showSubMenu: boolean;
  showTime?: boolean;
  onClick: () => void;
  onRescheduleClick: (prompt: string) => void;
  color?: string;
  category?: string;
};

export const liveEmoji = "🔴";

export function EventPreview({
  title,
  startTime,
  endTime,
  attendees,
  color,
  videoLink,
  isNow,
  showSubMenu,
  onClick,
  onRescheduleClick,
  category = "",
  showTime = true,
}: Props) {
  const startTimeFormatted = getEventPreviewFormattedTime(startTime);
  const ConferencingLinkIcon = getConferencingIcon(videoLink);
  const trackEvent = useTracking();
  const duration = endTime.diff(startTime, "minutes").minutes;

  return (
    <div
      className={classNames(
        "cw-flex cw-justify-between cw-gap-4 cw-items-center cw-my-0.5 cw-ml-1",
        endTime < DateTime.now() && "cw-transition-opacity hover:cw-opacity-100 cw-opacity-50",
      )}
    >
      <div className="cw-flex cw-font-body cw-gap-1 cw-items-center cw-min-w-[150px]">
        <Tooltip title={formatDuration(duration)} placement="bottom-start">
          <div
            className={classNames(
              "cw-text-muted cw-text-12 cw-font-medium cw-mr-1 cw-ml-3 cw-min-w-[54px] cw-text-left cw-cursor-default",
              !showTime && "cw-opacity-0",
            )}
          >
            {startTimeFormatted}
          </div>
        </Tooltip>
        <div className="cw-min-w-[150px]">
          <Button onClick={onClick} variant="text" sentiment="neutral" fullWidth>
            <div className="cw-flex cw-gap-1 cw-cursor-pointer cw-items-center cw-min-w-[100px] cw-mr-auto">
              {color && (
                <Tooltip title={category}>
                  <div
                    style={{ background: color }}
                    className="cw-w-[6px] cw-h-[80%] cw-rounded cw-self-stretch cw-shrink-0 cw-min-h-[20px] cw-mr-1"
                  />
                </Tooltip>
              )}
              <div className="cw-text-neutral cw-font-medium cw-overflow-hidden cw-text-ellipsis">
                {title}
              </div>
              {attendees.length > 1 && (
                <div>
                  <AvatarStack size="small" overlap maxShown={4}>
                    {sortAttendees(attendees).map((a, i) => (
                      <UserAvatar profile={a.profile} size="small" key={i} rsvp={a.rsvp} />
                    ))}
                  </AvatarStack>
                </div>
              )}
            </div>
          </Button>
        </div>
      </div>

      <div className="cw-flex cw-items-center cw-gap-0.5 cw-p-1 cw-rounded cw-bg-default cw-z-50">
        {videoLink?.uri && (
          <Tooltip title={"Join " + title} placement="bottom">
            <div>
              <Button
                variant="text"
                size="small"
                sentiment="neutral"
                onClick={() => {
                  trackEvent(TrackingEvents.CLOCKWISE_MINI.PLANNER_MINI.OPENED, {
                    videoLinkType: videoLink?.type,
                  });
                  window.open(videoLink?.uri, "_blank", "noopener noreferrer");
                }}
              >
                <div className="cw-flex cw-gap-0.5 cw-items-center">
                  {isNow && <span className="cw-animate-pulse cw-scale-75">{liveEmoji}</span>}
                  {ConferencingLinkIcon}
                </div>
              </Button>
            </div>
          </Tooltip>
        )}
        {showSubMenu && (
          <DropdownButton
            size="small"
            name="planner-settings"
            iconOpen={MoreVert}
            iconClosed={MoreVert}
          >
            <div className="cw-p-2">
              <Button onClick={() => onRescheduleClick("Reschedule " + title)} variant="outlined">
                Reschedule
              </Button>
            </div>
          </DropdownButton>
        )}
      </div>
    </div>
  );
}
